import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "lib/supabaseClient";
import Card from "components/card";
import {
  MdPeople,
  MdAssignmentInd,
  MdSearchOff,
  MdEmail,
  MdPhone,
  MdBadge,
  MdWork,
  MdOpenInNew,
} from "react-icons/md";

const getAvatarCacheKey = (url) =>
  url ? `core_avatar_cache_${btoa(url).slice(0, 120)}` : null;

const readCachedAvatar = (url) => {
  const key = getAvatarCacheKey(url);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeCachedAvatar = (url, dataUrl) => {
  const key = getAvatarCacheKey(url);
  if (!key || !dataUrl) return;
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    try {
      sessionStorage.setItem(key, dataUrl);
    } catch {}
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });

const fetchAvatarBlob = async (url) => {
  if (!url) return null;
  try {
    if (url.includes("supabase.co/storage/v1/object/public/")) {
      const path = url.split("/public/")[1];
      const bucket = path.split("/")[0];
      const filePath = path.split("/").slice(1).join("/");
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(filePath);
      if (!error && data) return data;
    }
    const response = await fetch(url, { cache: "force-cache" });
    return await response.blob();
  } catch {
    return null;
  }
};

const SafeAvatar = React.memo(({ url, wrapperClassName, imageClassName }) => {
  const [cachedUrl, setCachedUrl] = useState(() => readCachedAvatar(url));
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    setCachedUrl(readCachedAvatar(url));
  }, [url]);

  useEffect(() => {
    let isActive = true;
    let nextObjectUrl;
    const load = async () => {
      if (!url) {
        setObjectUrl(null);
        return;
      }
      const blob = await fetchAvatarBlob(url);
      if (!isActive) return;
      if (blob) {
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl && isActive) {
          writeCachedAvatar(url, dataUrl);
          setCachedUrl(dataUrl);
        }
      }
    };
    load();
    return () => {
      isActive = false;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [url]);

  const displayUrl =
    objectUrl || cachedUrl || "https://i.ibb.co/mRpg6P0/default-avatar.png";
  return (
    <div
      className={`relative select-none ${wrapperClassName}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 z-10 bg-transparent" />
      <div
        className={imageClassName}
        style={{
          backgroundImage: `url(${displayUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          pointerEvents: "none",
        }}
      />
    </div>
  );
});

export default function GlobalSearch() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(search).get("q");
  const [results, setResults] = useState({
    employees: [],
    leads: [],
    articles: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const performGlobalSearch = async () => {
      setLoading(true);
      const searchTerm = `%${query}%`;

      // جلب كافة بيانات الموظف لعرضها في نتائج البحث
      const { data: employees } = await supabase
        .from("employees")
        .select("*")
        .or(
          `full_name.ilike.${searchTerm},job_title.ilike.${searchTerm},employee_number.ilike.${searchTerm}`
        );

      const { data: leads } = await supabase
        .from("opportunities")
        .select("*")
        .or(`title.ilike.${searchTerm},customer_name.ilike.${searchTerm}`);

      const { data: articles } = await supabase
        .from("articles")
        .select("*")
        .ilike("title", searchTerm);

      setResults({
        employees: employees || [],
        leads: leads || [],
        articles: articles || [],
      });
      setLoading(false);
    };

    if (query) performGlobalSearch();
  }, [query]);

  const handleViewProfile = (id, name) => {
    // التوجه لصفحة البروفايل المهني باستخدام الاسم أو المعرف
    if (name) {
      navigate(`/admin/profile?name=${encodeURIComponent(name)}`);
    } else {
      navigate(`/admin/profile?id=${id}`);
    }
  };

  if (loading)
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        <p className="animate-pulse  uppercase tracking-widest text-brand-500">
          Scanning...
        </p>
      </div>
    );

  const totalResults =
    results.employees.length + results.leads.length + results.articles.length;

  return (
    <div className="animate-fadeIn mt-3 w-full">
      <header className="mb-10">
        <h2 className="text-4xl  uppercase  tracking-tighter text-core-charcoal">
          Search
        </h2>
        <p className="en-fix mt-2 text-[10px]  uppercase tracking-[0.4em] text-gray-500">
          <span className="text-brand-500">{totalResults}</span> Records
          synchronized for "{query}"
        </p>
      </header>

      {totalResults === 0 ? (
        <Card extra="flex flex-col items-center justify-center p-20 bg-white border border-gray-200 shadow-xl">
          <MdSearchOff size={60} className="mb-4 text-gray-800" />
          <p className="text-center text-xs  uppercase tracking-[0.2em] text-gray-500">
            No operational data found matching your query.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-10">
          {/* قسم الموظفين - بيانات كاملة */}
          {results.employees.length > 0 && (
            <section>
              <div className="mb-6 flex items-center gap-3 px-2">
                <MdPeople className="text-xl text-brand-500" />
                <h3 className="text-xs  uppercase tracking-[0.3em] text-core-charcoal">
                  Staff Registry
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {results.employees.map((emp) => (
                  <Card
                    key={emp.id}
                    extra="bg-white p-6 border border-gray-200 hover:border-brand-500/50 transition-all shadow-xl group"
                  >
                    <div className="flex flex-col gap-6 sm:flex-row">
                      <SafeAvatar
                        url={emp.avatar_url}
                        wrapperClassName="h-24 w-24"
                        imageClassName="h-full w-full rounded-3xl border-2 border-brand-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-xl  uppercase tracking-tighter text-core-charcoal">
                              {emp.full_name}
                            </h4>
                            <p className="en-fix mt-1 text-[10px]  uppercase tracking-widest text-brand-500">
                              ID: #{emp.employee_number}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              handleViewProfile(emp.id, emp.full_name)
                            }
                            className="rounded-xl bg-gray-100 p-2 text-core-charcoal shadow-sm transition-all hover:bg-brand-500 hover:text-white"
                            title="View Professional Profile"
                          >
                            <MdOpenInNew size={20} />
                          </button>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <MdWork className="text-sm text-gray-600" />
                            <p className="truncate text-[10px]  uppercase text-gray-400">
                              {emp.job_title || "---"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <MdBadge className="text-sm text-gray-600" />
                            <p className="truncate text-[10px]  uppercase text-gray-400">
                              {emp.department || "---"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <MdEmail className="text-sm text-gray-600" />
                            <p className="en-fix truncate text-[10px]  lowercase text-gray-400">
                              {emp.personal_email || "---"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <MdPhone className="text-sm text-gray-600" />
                            <p className="en-fix truncate text-[10px]  uppercase text-gray-400">
                              {emp.phone_number || "---"}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() =>
                            handleViewProfile(emp.id, emp.full_name)
                          }
                          className="mt-6 w-full rounded-xl border border-gray-200 bg-gray-50 py-3 text-[9px]  uppercase tracking-[0.3em] text-core-charcoal transition-all hover:bg-gray-100"
                        >
                          View Professional Profile Details
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* الأقسام الأخرى (Leads & Articles) تظل مختصرة */}
          {results.leads.length > 0 && (
            <section>
              <div className="mb-6 flex items-center gap-3 px-2">
                <MdAssignmentInd className="text-emerald-500 text-xl" />
                <h3 className="text-xs  uppercase tracking-[0.3em] text-core-charcoal">
                  Opportunities
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {results.leads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => navigate(`/admin/crm?id=${lead.id}`)}
                    className="hover:border-emerald-500 cursor-pointer rounded-2xl border border-gray-200 bg-white p-5 shadow-xl transition-all"
                  >
                    <p className="text-sm  uppercase text-white">
                      {lead.title}
                    </p>
                    <p className="mt-1 text-[9px]  uppercase text-gray-500">
                      {lead.customer_name || "Direct Client"}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
